import { GroupContext, SystemContext } from "@Totum/Main/Constants";
import { MainContext } from "@Totum/Main/Context/provider";
import useUserAssignmentMenuOperations from "@Totum/Groups/Application/hooks/useUserAssignmentMenuOperations";
import useUserListOperations from "@Totum/Groups/Application/hooks/useUserListOperations";
import { ActiveTabKey } from "@Totum/Groups/Constants";
import useCurrentGroupId from "@Totum/Groups/State/hooks/useCurrentGroupId";
import { checkCapWithContext, findLowestContext, hasCapability } from "@Totum/Main/Utils";
import { useContext } from "react";

/**
 * Presents the `UserView` component with the UI model and handlers.
 */
const useUserViewPresenter = (
    activeTabKey: string,
    currentPage?: number,
    searchTerm?: string,
    isRootGroupView?: boolean
) => {
    const pageSize = 10;
    const [currentGroupId] = useCurrentGroupId();
    const allowRemove = hasCapability("UserGroup/RemoveMembers", GroupContext, currentGroupId);
    const canAddToGroups = hasCapability("UserGroup/AddMembers", GroupContext, currentGroupId);
    const canRemoveBase = findLowestContext("UserGroup/RemoveMembers") === SystemContext;
    const isGroupAdmin = checkCapWithContext("Account/Create", GroupContext, currentGroupId);

    // ** Hooks ** //
    const { groupUsers, isFetchingGroupUsers, allUsers, isFetchingAllUsers, removeGroupMember } = useUserListOperations(
        currentPage,
        pageSize,
        searchTerm,
        activeTabKey,
        isGroupAdmin
    );

    // ** Constants & Variables ** //
    const { setCurrentUser } = useContext(MainContext);

    const userIds =
        activeTabKey === ActiveTabKey.Groups
            ? groupUsers?.users?.map((x) => x.userId)
            : allUsers?.users?.map((x) => x.userId);

    const { groupUserAddAction, userSearchAddAction, creditAssignments, roleTags, roleAssignments } =
        useUserAssignmentMenuOperations(activeTabKey, userIds);

    // ** Handlers ** //
    const handleUserSelect = (userId: string): void => {
        setCurrentUser({ instance: userId });
    };

    const handleRemoveGroupMember = (userIds: string[]) => {
        removeGroupMember.mutateAsync(userIds);
    };

    // ** Exports ** //

    return {
        handlers: {
            handleUserSelect,
            handleRemoveGroupMember: !isRootGroupView || canRemoveBase ? handleRemoveGroupMember : null,
            userIds,
        },
        uiModel: {
            groupUserAddAction,
            userSearchAddAction,
            creditAssignments,
            roleTags,
            roleAssignments,
            pageSize,

            //capabilities
            allowRemove,
            canAddToGroups,

            //data
            groupUsers,
            isFetchingGroupUsers,
            allUsers,
            isFetchingAllUsers,
        },
    };
};

export default useUserViewPresenter;
